import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TableContainer,
  Select,
  MenuItem,
  Box,
  FormControl,
  InputLabel,
  Toolbar
} from '@mui/material';
import { API } from '../config';


const STATUSES = ['CREATED', 'ACCEPTED', 'IN_TRANSIT', 'DELIVERED', 'CANCELLED'];

export default function AdminDashboard() {
  const history = useHistory();
  const [orders, setOrders] = useState([]);
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const token = localStorage.getItem('token');
  const role = (localStorage.getItem('role') || '').toLowerCase();

  // Guard: must be admin
  useEffect(() => {
    if (!token || role !== 'admin') {
      history.replace('/admin/sign-in');
      return;
    }
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchOrders() {
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 401 || res.status === 403) {
        history.replace('/admin/sign-in');
        return;
      }
      const data = await res.json();
      setOrders(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('GET /api/admin/orders failed', e);
    } finally {
      setLoading(false);
    }
  }

  async function updateStatus(id, nextStatus) {
    try {
      setSavingId(id);
      const res = await fetch(`${API}/api/admin/orders/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ nextStatus })
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${res.status}`);
      }
      // Refresh list after successful update
      fetchOrders();
    } catch (e) {
      alert(`Failed to update: ${e.message}`);
    } finally {
      setSavingId(null);
    }
  }

  function signOut() {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('adminEmail');
    history.replace('/admin/sign-in');
  }

  const visible = useMemo(() => {
    if (filterStatus === 'ALL') return orders;
    return orders.filter(o => o.status === filterStatus);
  }, [orders, filterStatus]);

  return (
    <Box p={2}>
      <Toolbar disableGutters style={{ display: 'flex', gap: 12, justifyContent: 'space-between' }}>
        <Typography variant="h5">Admin — Orders</Typography>
        <Box display="flex" gap={12} alignItems="center">
          <FormControl size="small">
            <InputLabel id="filter-status-label">Filter</InputLabel>
            <Select
              labelId="filter-status-label"
              label="Filter"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{ minWidth: 160 }}
            >
              <MenuItem value="ALL">All statuses</MenuItem>
              {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
          <Button onClick={fetchOrders} variant="outlined">Refresh</Button>
          <Button onClick={signOut} variant="text" color="error">Sign out</Button>
        </Box>
      </Toolbar>

      <TableContainer component={Paper} style={{ marginTop: 12 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="8%">ID</TableCell>
              <TableCell width="28%">From → To</TableCell>
              <TableCell width="14%">Delivery Type</TableCell>
              <TableCell width="14%">Truck Req.</TableCell>
              <TableCell width="12%">Weight (lbs)</TableCell>
              <TableCell width="12%">Status</TableCell>
              <TableCell width="12%">Change</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7}>Loading…</TableCell></TableRow>
            ) : visible.length === 0 ? (
              <TableRow><TableCell colSpan={7}>No orders.</TableCell></TableRow>
            ) : (
              visible.map(o => (
                <TableRow key={o.id}>
                  <TableCell>#{o.id}</TableCell>
                  <TableCell>{o.from_address} → {o.to_address}</TableCell>
                  <TableCell>{o.delivery_type || 'standard'}</TableCell>
                  <TableCell>{o.required_truck_type || 'Any'}</TableCell>
                  <TableCell>{o.weight ?? '—'}</TableCell>
                  <TableCell>{o.status}</TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={o.status}
                      onChange={(e) => updateStatus(o.id, e.target.value)}
                      disabled={savingId === o.id}
                    >
                      {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </Select>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
